/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import StatusBadge from '@/shared/ui/components/basic/StatusBadge';
import type { StatusBadgeTone } from '@/shared/ui/components/basic/StatusBadge';
import {
  DealsService,
  type DealProposal,
  type DealProposalStatus,
} from '@/services/requests/housien-services/DealsService';
import { formatLocalizedDate } from '@/shared/utils/date';
import { formatDealMoney } from '../utils/deal-state';

/** One offer, plus the attendance it belongs to. */
export interface ProposalWithAttendance extends DealProposal {
  deal_id: string;
  client_name?: string | null;
}

interface EntityProposalsSectionProps {
  agencyId: string;
  /** Offers on this property, whoever made them. */
  propertyId?: string;
  /** Offers by this client, on whatever property. */
  clientId?: string;
  /** Bumped by the caller to refetch after recording. */
  refreshKey?: number;
  /**
   * Reports how many offers were found, so a caller that shows a count
   * beside this section (the property tabs) does not have to load them
   * a second time. Read-only: it never changes what is rendered.
   */
  onCountChange?: (count: number) => void;
}

/** Offer status mapped onto the design-system badge tones. */
const STATUS_TONE: Record<DealProposalStatus, StatusBadgeTone> = {
  sent: 'neutral',
  countered: 'warning',
  refused: 'error',
  accepted: 'success',
};

/**
 * Reads the offers recorded against a property or a client.
 *
 * There is no endpoint for "offers on X": offers live inside the
 * attendance, so this lists the attendances filtered by property or
 * client and flattens theirs. Filtering by property afterwards matters —
 * an attendance holds several properties and only some of its offers are
 * about this one.
 */
function useEntityProposals(
  agencyId: string,
  propertyId?: string,
  clientId?: string,
  refreshKey = 0,
) {
  const [proposals, setProposals] = useState<ProposalWithAttendance[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!agencyId || (!propertyId && !clientId)) return;
    setLoading(true);
    try {
      const listed = await DealsService.listDeals(
        agencyId, 1, 100, undefined, clientId,
        undefined, undefined, undefined, propertyId,
      );
      const flattened = (listed.data ?? []).flatMap((deal) =>
        (deal.proposals ?? [])
          .filter((p) => !propertyId || p.property_id === propertyId)
          .map((p) => ({
            ...p,
            deal_id: deal.deal_id,
            client_name: deal.client_name,
          })),
      );
      flattened.sort((a, b) => b.created_at.localeCompare(a.created_at));
      setProposals(flattened);
    } finally {
      setLoading(false);
    }
  }, [agencyId, propertyId, clientId, refreshKey]);

  useEffect(() => { void load(); }, [load]);

  return { proposals, loading };
}

/**
 * The Propostas section of a property or a client page.
 *
 * Read-only by itself. The property page passes no action, because an
 * offer is not created from a property: it needs a client and an
 * attendance to belong to. The client page passes one, because there the
 * client is already known and only the property is missing.
 *
 * @param props - The agency, the entity to filter by, and the optional
 *   record action.
 * @returns The proposals section.
 */
export function EntityProposalsSection({
  agencyId, propertyId, clientId, refreshKey, onCountChange,
}: EntityProposalsSectionProps) {
  const { t } = useTranslation('features/deals');
  const { proposals, loading } = useEntityProposals(
    agencyId, propertyId, clientId, refreshKey,
  );

  useEffect(() => {
    if (!loading) onCountChange?.(proposals.length);
  }, [loading, proposals.length, onCountChange]);

  if (loading) {
    return (
      <div className="flex flex-col gap-[var(--space-3)]">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-[52px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse" />
        ))}
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div
        data-testid="entity-proposals-empty"
        className="flex flex-col items-center justify-center gap-(--space-1) h-[120px] text-center"
      >
        <Text component="span" className="text-[length:var(--text-body-sm)] text-(--text-tertiary)">
          {t('entityProposals.empty')}
        </Text>
        <Text component="span" className="max-w-80 text-[length:var(--text-meta)] leading-normal text-(--text-tertiary)">
          {propertyId ? t('entityProposals.emptyHintProperty') : t('entityProposals.emptyHintClient')}
        </Text>
      </div>
    );
  }

  return (
    <div
      data-testid="entity-proposals-section"
      className="flex flex-col gap-[var(--space-3)]"
    >
      {proposals.map((proposal) => (
        <ProposalRow
          key={proposal.proposal_id}
          proposal={proposal}
          agencyId={agencyId}
          showClientName={Boolean(propertyId)}
          t={t}
        />
      ))}
    </div>
  );
}

interface ProposalRowProps {
  proposal: ProposalWithAttendance;
  agencyId: string;
  /** The property view lists offers from many clients, so it names each one. */
  showClientName: boolean;
  t: TFunction;
}

/**
 * One offer, rendered with the same surface, typography and trailing badge as
 * the visit and property-of-interest rows it sits beside.
 *
 * @param props - The offer, the agency, and display copy.
 * @returns The rendered proposal row.
 */
function ProposalRow({ proposal, agencyId, showClientName, t }: ProposalRowProps) {
  const money = formatDealMoney(proposal.value) ?? '—';
  const meta = [
    formatLocalizedDate(proposal.created_at),
    showClientName ? proposal.client_name : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      data-testid="entity-proposal-row"
      className="flex justify-between items-center gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-subtle)]"
    >
      <div className="min-w-0 flex-1">
        <Text component="span" className="block truncate text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)] tabular-nums text-[var(--text-primary)]">
          {money} · {t(`types.${proposal.deal_type}`)}
        </Text>
        <Text component="span" className="block truncate text-[length:var(--text-micro)] tabular-nums text-[var(--text-tertiary)]">
          {meta}
        </Text>
      </div>

      <div className="flex shrink-0 items-center gap-[var(--space-3)]">
        <StatusBadge tone={STATUS_TONE[proposal.status]}>
          {t(`detail.proposals.statuses.${proposal.status}`)}
        </StatusBadge>
        {/* Answering, correcting and accepting all live on the
            attendance. Here the offer is shown, not managed. */}
        <Link
          to={`/app/agencies/${agencyId}/deals/${proposal.deal_id}`}
          className="whitespace-nowrap text-[length:var(--text-meta)] text-(--seed-base) no-underline hover:opacity-80"
        >
          {t('entityProposals.openAttendance')}
        </Link>
      </div>
    </div>
  );
}
